import React, { useRef } from "react";
import { motion, useInView } from "framer-motion";
import { Monitor, Smartphone, Terminal, Database, LayoutTemplate, MonitorSmartphone } from "lucide-react";

// Floating icon only animates when parent is in view
const FloatingIcon = ({ icon: Icon, delay, x, y, size = 40, color, bg, isVisible }) => (
    <motion.div
        initial={{ opacity: 0, scale: 0 }}
        animate={isVisible ? { opacity: 1, scale: 1, y: [0, -12, 0] } : { opacity: 0, scale: 0 }}
        transition={isVisible ? {
            opacity: { duration: 0.6, delay },
            scale: { duration: 0.6, delay, type: "spring" },
            y: { duration: 4, repeat: Infinity, ease: "easeInOut", delay },
        } : { duration: 0.3 }}
        className={`absolute ${x} ${y} flex items-center justify-center rounded-2xl border border-white/10 backdrop-blur-md shadow-2xl z-20`}
        style={{ width: size, height: size, background: bg }}
    >
        <Icon className="text-white drop-shadow-md" size={size * 0.5} color={color} />
    </motion.div>
);

const AnimatedHeroGraphic = () => {
    const ref = useRef(null);
    // Only animate when hero is actually visible — stops burning GPU while user scrolls down
    const isVisible = useInView(ref, { amount: 0.1 });

    return (
        <div ref={ref} className="relative w-full max-w-lg aspect-square flex flex-col justify-center items-center gap-6 z-10">
            {/* Background Glow — only pulse when visible */}
            <div
                className={`absolute top-1/2 left-1/2 -translate-x-1/2 -translate-y-1/2 w-[150%] h-[150%] bg-gradient-to-tr from-designColor/15 via-purple-500/10 to-cyan-500/10 blur-[100px] rounded-full z-0 pointer-events-none transition-opacity duration-500 ${isVisible ? 'opacity-100 animate-pulse' : 'opacity-0'}`}
            />

            {/* Main IDE Window */}
            <motion.div
                initial={{ y: 50, opacity: 0, rotateX: 10 }}
                animate={{ y: 0, opacity: 1, rotateX: 0 }}
                transition={{ duration: 1, type: "spring", bounce: 0.3 }}
                className="relative z-20 w-full max-w-[440px] rounded-[1.5rem] border border-white/10 bg-gradient-to-b from-[#1a1a1e]/90 to-[#121214]/90 backdrop-blur-md shadow-[0_30px_60px_rgba(0,0,0,0.6),inset_0_1px_0_rgba(255,255,255,0.1)] overflow-hidden"
            >
                {/* IDE Header */}
                <div className="flex items-center px-5 py-3.5 border-b border-white/[0.05] bg-white/[0.01]">
                    <div className="flex gap-2">
                        <motion.div whileHover={{ scale: 1.2 }} className="w-3 h-3 rounded-full bg-[#ff5f56]" />
                        <motion.div whileHover={{ scale: 1.2 }} className="w-3 h-3 rounded-full bg-[#ffbd2e]" />
                        <motion.div whileHover={{ scale: 1.2 }} className="w-3 h-3 rounded-full bg-[#27c93f]" />
                    </div>
                    <div className="mx-auto flex items-center gap-2 px-6 py-1.5 rounded-full bg-black/40 border border-white/5 shadow-inner">
                        <Terminal size={12} className="text-gray-400" />
                        <span className="text-[11px] sm:text-xs text-gray-400 font-mono tracking-wider">portfolio.tsx</span>
                    </div>
                    <div className="w-10"></div>
                </div>

                {/* IDE Content */}
                <div className="p-6 sm:p-8 font-mono text-sm sm:text-[15px] leading-8 sm:leading-[2.2rem]">
                    <motion.div initial={{ opacity: 0, x: -10 }} animate={{ opacity: 1, x: 0 }} transition={{ delay: 0.5 }}>
                        <span className="text-[#ff7b72] font-medium">const</span> <span className="text-[#79c0ff] font-medium">developer</span> <span className="text-[#c9d1d9]">=</span> <span className="text-[#ffd700]">&#123;</span>
                    </motion.div>

                    <motion.div initial={{ opacity: 0, x: -10 }} animate={{ opacity: 1, x: 0 }} transition={{ delay: 0.8 }} className="pl-6 sm:pl-8">
                        <span className="text-[#8b949e]">name:</span> <span className="text-[#a5d6ff]">&quot;Aditya&quot;</span><span className="text-[#8b949e]">,</span>
                    </motion.div>

                    <motion.div initial={{ opacity: 0, x: -10 }} animate={{ opacity: 1, x: 0 }} transition={{ delay: 1.1 }} className="pl-6 sm:pl-8">
                        <span className="text-[#8b949e]">role:</span> <span className="text-[#a5d6ff]">&quot;Sr. Software Developer&quot;</span><span className="text-[#8b949e]">,</span>
                    </motion.div>

                    <motion.div initial={{ opacity: 0, x: -10 }} animate={{ opacity: 1, x: 0 }} transition={{ delay: 1.4 }} className="pl-6 sm:pl-8 flex flex-wrap items-center">
                        <span className="text-[#8b949e] mr-2">skills:</span>
                        <span className="text-[#ffd700] mr-1">[</span>
                        <motion.span whileHover={{ scale: 1.05, color: "#61DAFB" }} className="text-[#a5d6ff] cursor-pointer">{`"React"`}</motion.span><span className="text-[#8b949e] mr-1">,</span>
                        <motion.span whileHover={{ scale: 1.05, color: "#61DAFB" }} className="text-[#a5d6ff] cursor-pointer">{`"React Native"`}</motion.span><span className="text-[#8b949e] mr-1">,</span>
                        <motion.span whileHover={{ scale: 1.05, color: "#40873F" }} className="text-[#a5d6ff] cursor-pointer">{`"Node.js"`}</motion.span><span className="text-[#8b949e] mr-1">,</span>
                        <motion.span whileHover={{ scale: 1.05, color: "#40873F" }} className="text-[#a5d6ff] cursor-pointer">{`"AWS"`}</motion.span>
                        <span className="text-[#ffd700]">]</span><span className="text-[#8b949e]">,</span>
                    </motion.div>

                    <motion.div initial={{ opacity: 0, x: -10 }} animate={{ opacity: 1, x: 0 }} transition={{ delay: 1.7 }} className="pl-6 sm:pl-8">
                        <span className="text-[#8b949e]">expertise:</span> <span className="text-[#a5d6ff]">&quot;Web & Mobile Apps&quot;</span>
                    </motion.div>

                    <motion.div initial={{ opacity: 0, x: -10 }} animate={{ opacity: 1, x: 0 }} transition={{ delay: 2.0 }}>
                        <span className="text-[#ffd700]">&#125;</span><span className="text-[#8b949e]">;</span>
                        {/* Blinking cursor — only blink when visible */}
                        {isVisible && (
                            <motion.div
                                animate={{ opacity: [1, 0] }}
                                transition={{ duration: 0.8, repeat: Infinity }}
                                className="inline-block w-2.5 h-5 bg-designColor ml-2 align-middle translate-y-[-2px]"
                            />
                        )}
                    </motion.div>
                </div>

                {/* Decorative Bottom Bar */}
                <div className="absolute bottom-0 left-0 w-full h-[3px] bg-gradient-to-r from-designColor via-purple-500 to-cyan-500" />
            </motion.div>

            {/* Info Cards Panel */}
            <motion.div
                initial={{ opacity: 0, y: 30 }}
                animate={{ opacity: 1, y: 0 }}
                transition={{ duration: 0.8, delay: 2.2, type: "spring" }}
                className="relative z-20 w-full max-w-[440px] flex gap-3 sm:gap-4"
            >
                {/* Web Card */}
                <motion.div
                    whileHover={{ y: -5, scale: 1.02 }}
                    className="flex-1 group relative rounded-2xl border border-white/[0.08] bg-gradient-to-b from-[#1a1a1e]/80 to-[#121214]/80 backdrop-blur-sm p-3 sm:p-4 flex items-center gap-3 sm:gap-4 transition-all duration-300 hover:border-designColor/50 shadow-[0_10px_30px_rgba(0,0,0,0.3)] hover:shadow-[0_15px_40px_rgba(255,1,79,0.2)] text-left"
                >
                    <div className="absolute inset-0 bg-gradient-to-b from-white/[0.02] to-transparent rounded-2xl pointer-events-none" />
                    <div className="relative z-10 shrink-0 w-10 h-10 sm:w-12 sm:h-12 rounded-xl bg-white/[0.03] flex items-center justify-center border border-white/[0.05] group-hover:border-designColor/40 group-hover:bg-designColor/10 transition-colors shadow-inner">
                        <LayoutTemplate className="w-5 h-5 sm:w-6 sm:h-6 text-gray-400 group-hover:text-designColor transition-colors" />
                    </div>
                    <div className="relative z-10 flex flex-col justify-center">
                        <p className="text-[9px] sm:text-[10px] font-bold uppercase tracking-[0.2em] text-gray-500 mb-0.5 group-hover:text-gray-400 transition-colors leading-none">Web</p>
                        <p className="text-[13px] sm:text-[14px] font-bold text-gray-200 mb-0.5 group-hover:text-white transition-colors leading-tight">Full Stack</p>
                        <p className="text-[9px] sm:text-[10px] text-gray-500 group-hover:text-gray-300 transition-colors leading-none">APIs · React · Node</p>
                    </div>
                </motion.div>

                {/* Mobile Card */}
                <motion.div
                    whileHover={{ y: -5, scale: 1.02 }}
                    className="flex-1 group relative rounded-2xl border border-white/[0.08] bg-gradient-to-b from-[#1a1a1e]/80 to-[#121214]/80 backdrop-blur-sm p-3 sm:p-4 flex items-center gap-3 sm:gap-4 transition-all duration-300 hover:border-designColor/50 shadow-[0_10px_30px_rgba(0,0,0,0.3)] hover:shadow-[0_15px_40px_rgba(255,1,79,0.2)] text-left"
                >
                    <div className="absolute inset-0 bg-gradient-to-b from-white/[0.02] to-transparent rounded-2xl pointer-events-none" />
                    <div className="relative z-10 shrink-0 w-10 h-10 sm:w-12 sm:h-12 rounded-xl bg-white/[0.03] flex items-center justify-center border border-white/[0.05] group-hover:border-designColor/40 group-hover:bg-designColor/10 transition-colors shadow-inner">
                        <MonitorSmartphone className="w-5 h-5 sm:w-6 sm:h-6 text-gray-400 group-hover:text-designColor transition-colors" />
                    </div>
                    <div className="relative z-10 flex flex-col justify-center">
                        <p className="text-[9px] sm:text-[10px] font-bold uppercase tracking-[0.2em] text-gray-500 mb-0.5 group-hover:text-gray-400 transition-colors leading-none">Mobile</p>
                        <p className="text-[13px] sm:text-[14px] font-bold text-gray-200 mb-0.5 group-hover:text-white transition-colors leading-tight">Cross-platform</p>
                        <p className="text-[9px] sm:text-[10px] text-gray-500 group-hover:text-gray-300 transition-colors leading-none">React Native · iOS · Android</p>
                    </div>
                </motion.div>
            </motion.div>

            <motion.p
                initial={{ opacity: 0 }}
                animate={{ opacity: 1 }}
                transition={{ delay: 2.5 }}
                className="relative z-20 text-center text-[10px] sm:text-[11px] text-gray-500 font-medium uppercase tracking-[0.25em] mt-2"
            >
                <span className="text-designColor/80">5+ years</span> · Delightful experiences
            </motion.p>

            {/* Floating Elements — pause when off-screen */}
            <FloatingIcon icon={Monitor} delay={2.2} x="-left-8 sm:-left-12" y="top-20" size={48} color="#fff" bg="linear-gradient(135deg, rgba(8,145,178,0.15) 0%, rgba(8,145,178,0.05) 100%)" isVisible={isVisible} />
            <FloatingIcon icon={Smartphone} delay={2.4} x="-right-8 sm:-right-12" y="top-40" size={56} color="#fff" bg="linear-gradient(135deg, rgba(255,1,79,0.15) 0%, rgba(255,1,79,0.05) 100%)" isVisible={isVisible} />
            <FloatingIcon icon={Database} delay={2.6} x="-left-4 sm:-left-8" y="bottom-36 sm:bottom-48" size={44} color="#fff" bg="linear-gradient(135deg, rgba(168,85,247,0.15) 0%, rgba(168,85,247,0.05) 100%)" isVisible={isVisible} />

            {/* Orbits — only rotate when visible */}
            <motion.div
                animate={isVisible ? { rotate: 360 } : { rotate: 0 }}
                transition={isVisible ? { duration: 40, repeat: Infinity, ease: "linear" } : { duration: 0 }}
                className="absolute w-[90%] h-[90%] border-[0.5px] border-white/[0.03] rounded-full pointer-events-none z-0"
            />
            <motion.div
                animate={isVisible ? { rotate: -360 } : { rotate: 0 }}
                transition={isVisible ? { duration: 50, repeat: Infinity, ease: "linear" } : { duration: 0 }}
                className="absolute w-[110%] h-[110%] border-[0.5px] border-white/[0.02] rounded-full border-dashed pointer-events-none z-0"
            />
        </div>
    );
};

export default AnimatedHeroGraphic;
